import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui';
import { Plus, Upload, Briefcase, Clock, CheckCircle2 } from 'lucide-react';
import { NewServiceModal, type NewServiceInitialData } from '@/components/features/new-service-modal';
import { BulkImportModal } from '@/components/features/bulk-import-modal';

import { ServiceCardPremium } from '@/components/features/provider/service-card-premium';
import { Skeleton } from '@/components/ui/skeleton';
import { useServiceFilters } from '@/hooks/use-service-filters';
import { ServiceFiltersToolbar } from '@/components/features/provider/service-filters-toolbar';
import { cn } from '@/lib/utils';
import { toast } from '@template/lib/toast';
import { type ServiceType } from '@/types/validators';
import { type SectionHeaderProps, type EmptyStateProps } from '@/types/forms';
import type { Id } from '../../convex/_generated/dataModel';
import { getDurationBucket, trackServiceCreated, trackServiceCreationFailed } from '@/lib/analytics';

const SectionHeader = ({ title, count, icon: Icon, variant = 'active' }: SectionHeaderProps): React.JSX.Element => {
  const variantStyles = {
    active: 'text-primary',
    past: 'text-warning',
    completed: 'text-muted-foreground',
  };

  return (
    <div className='flex items-center gap-3 mb-3'>
      <div className={cn('flex items-center justify-center size-8 rounded-lg', variant === 'active' && 'bg-primary/10', variant === 'past' && 'bg-warning/10', variant === 'completed' && 'bg-muted')}>
        <Icon className={cn('size-4', variantStyles[variant])} aria-hidden='true' />
      </div>
      <div className='flex items-baseline gap-2'>
        <h2 className='text-lg font-medium text-foreground'>{title}</h2>
        <span
          className={cn(
            'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded text-xs font-medium',
            variant === 'active' && 'bg-primary/10 text-primary',
            variant === 'past' && 'bg-warning/10 text-warning-foreground',
            variant === 'completed' && 'bg-muted text-muted-foreground'
          )}
        >
          {count}
        </span>
      </div>
    </div>
  );
};

/**
 * Empty state component
 */
const EmptyState = ({ variant, hasFilters }: EmptyStateProps): React.JSX.Element => {
  const messages = {
    active: hasFilters ? 'Keine aktiven Dienste entsprechen den Filterkriterien.' : 'Erstellen Sie Ihren ersten Dienst, um Vertretungen zu finden.',
    past: hasFilters ? 'Keine vergangenen Dienste entsprechen den Filterkriterien.' : 'Keine vergangenen Dienste mit offenen Aufgaben.',
    completed: hasFilters ? 'Keine abgeschlossenen Dienste entsprechen den Filterkriterien.' : 'Abgeschlossene Dienste werden hier angezeigt.',
  };

  return (
    <div className='rounded-lg border border-border bg-card p-8 text-center'>
      <p className='text-sm text-muted-foreground'>{messages[variant]}</p>
    </div>
  );
};

/**
 * Loading skeleton
 */
const ServiceCardSkeleton = (): React.JSX.Element => (
  <div className='rounded-lg border border-border bg-card p-4 space-y-3'>
    <div className='flex items-center gap-2'>
      <Skeleton className='h-5 w-28 rounded' />
      <Skeleton className='h-5 w-20 rounded' />
    </div>
    <Skeleton className='h-5 w-3/4' />
    <div className='flex justify-between items-center'>
      <Skeleton className='h-4 w-24' />
      <Skeleton className='h-6 w-16' />
    </div>
  </div>
);

const ProviderServicesPage = () => {
  // Use enhanced queries for provider duties
  const myDuties = useQuery(api.app.providerServiceQueries.getEnhancedProviderDuties, {}) || [];
  const metrics = useQuery(api.app.providerServiceQueries.getProviderServiceMetrics, {}) || [];
  const marketingDrafts = useQuery(api.app.providerServiceDrafts.listMyActiveProviderServiceDrafts, {}) || [];

  const createDuty = useMutation(api.app.duties.mutations.createDuty as any);
  const consumeProviderServiceDraft = useMutation(api.app.providerServiceDrafts.consumeProviderServiceDraft);
  const [showNewModal, setShowNewModal] = React.useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = React.useState(false);
  const [pendingServiceData, setPendingServiceData] = React.useState<NewServiceInitialData | undefined>(undefined);

  // Create metrics lookup map
  const metricsMap = React.useMemo(() => {
    const map = new Map<
      string,
      {
        applicationCount: number;
        documentCount: number;
        hasUnreadMessages: boolean;
      }
    >();
    metrics.forEach((m: any) => {
      map.set(m.dutyId, {
        applicationCount: m.applicationCount,
        documentCount: m.documentCount,
        hasUnreadMessages: m.hasUnreadMessages,
      });
    });
    return map;
  }, [metrics]);

  // Use service filters hook for filtering functionality
  const { filters, setSearch, setStatus, setServiceType, setDateRange, filteredDuties, hasActiveFilters, clearFilters, filteredCount, totalCount } = useServiceFilters(myDuties);

  // Partition filtered duties into Active (upcoming & not completed), Past (ended but not completed), Completed
  const now = Date.now();
  const sortedFilteredDuties = filteredDuties.slice().sort((a: any, b: any) => a.startDate - b.startDate);
  const active = sortedFilteredDuties.filter((d: any) => d.endDate > now && d.status !== 'completed' && d.status !== 'cancelled');
  const pastNotCompleted = sortedFilteredDuties.filter((d: any) => d.endDate <= now && d.status !== 'completed' && d.status !== 'cancelled');
  const completed = sortedFilteredDuties.filter((d: any) => d.status === 'completed');

  // Loading state
  const isLoading = myDuties === undefined || metrics === undefined;

  // Check if all sections are empty due to active filters
  const noFilteredResults = hasActiveFilters && filteredDuties.length === 0;

  // First run: no duties at all and no filters applied. Show one welcoming empty state
  // with a single clear action instead of three separate empty section cards.
  const isFirstRun = !isLoading && totalCount === 0 && !hasActiveFilters;

  // Count services requiring attention
  const attentionCount = active.filter((d: any) => d.status === 'pending_payment' || d.status === 'pending_contract').length;
  const latestDraft = marketingDrafts[0];

  const openDraftInModal = React.useCallback((draft: any) => {
    const initialData: NewServiceInitialData = {
      draftId: draft.draftId as Id<'providerServiceDrafts'>,
      sourceKey: draft.sourceKey,
      serviceType: draft.serviceType as ServiceType,
      startDate: draft.startDate,
      endDate: draft.endDate,
      compensationType: draft.compensationType,
      hasNotfallscheinbeteiligung: draft.hasNotfallscheinbeteiligung,
      ...(draft.serviceAreaId ? { serviceAreaId: draft.serviceAreaId } : {}),
      ...(draft.manualServiceArea ? { manualServiceArea: draft.manualServiceArea } : {}),
      ...(draft.manualBereitschaftspraxis ? { manualBereitschaftspraxis: draft.manualBereitschaftspraxis } : {}),
      ...(draft.manualFahrdienstgebiet ? { manualFahrdienstgebiet: draft.manualFahrdienstgebiet } : {}),
      ...(draft.fixedPrice !== undefined ? { fixedPrice: draft.fixedPrice } : {}),
      ...(draft.minPrice !== undefined ? { minPrice: draft.minPrice } : {}),
      ...(draft.maxPrice !== undefined ? { maxPrice: draft.maxPrice } : {}),
      ...(draft.notfallscheinbeteiligungRate !== undefined ? { notfallscheinbeteiligungRate: draft.notfallscheinbeteiligungRate } : {}),
      ...(draft.notes ? { notes: draft.notes } : {}),
    };

    setPendingServiceData(initialData);
    setShowNewModal(true);
    toast.success('Gespeicherter Entwurf geladen.');
  }, []);

  return (
    <div className='bg-background overflow-x-hidden'>
      {/* Header */}
      <header className='sticky top-0 z-40 bg-background border-b border-border'>
        <div className='px-4 sm:px-6 py-3 sm:py-4 max-w-6xl mx-auto'>
          <div className='flex items-center justify-between gap-4'>
            {/* Left: Title */}
            <div className='flex items-center gap-3'>
              <h1 className='text-lg sm:text-xl font-semibold text-foreground tracking-tight'>Dienste</h1>
            </div>

            {/* Right: Actions */}
            <div className='flex items-center gap-2'>
              {attentionCount > 0 && (
                <div className='hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md bg-warning/10 border border-warning/20'>
                  <span className='inline-flex rounded-full h-2 w-2 bg-warning' />
                  <span className='text-sm font-medium text-warning-foreground'>
                    {attentionCount} {attentionCount === 1 ? 'Aktion' : 'Aktionen'} erforderlich
                  </span>
                </div>
              )}
              <Button onClick={() => setShowBulkImportModal(true)} variant='outline' size='sm' className='hidden sm:flex items-center gap-2'>
                <Upload className='h-4 w-4' />
                CSV-Import
              </Button>
              <Button onClick={() => setShowNewModal(true)} size='sm' className='flex items-center gap-2'>
                <Plus className='h-4 w-4' />
                <span className='hidden sm:inline'>Neuer Dienst</span>
                <span className='sm:hidden'>Neu</span>
              </Button>
              <NewServiceModal
                open={showNewModal}
                onOpenChange={(v) => {
                  setShowNewModal(v);
                  // Clear pending data when modal is closed
                  if (!v) setPendingServiceData(undefined);
                }}
                onSubmit={async (data: any) => {
                  const analyticsPayload = {
                    service_type: data.serviceType,
                    compensation_type: data.priceMode === 'range' ? 'range' : 'fixed',
                    duration_bucket: getDurationBucket(data.startDate, data.endDate),
                    source: 'provider_services' as const,
                  };

                  try {
                    const dutyId = await createDuty({
                      title: data.title,
                      description: data.description,
                      privateNotes: data.privateNotes,
                      startDate: data.startDate,
                      endDate: data.endDate,
                      serviceType: data.serviceType,
                      location: data.location,
                      compensationType: data.priceMode === 'range' ? 'range' : 'fixed',
                      minPrice: data.priceMode === 'range' ? Number(data.minPrice) : Number(data.hourly ?? data.compensation),
                      ...(data.priceMode === 'range' && data.maxPrice ? { maxPrice: Number(data.maxPrice) } : {}),
                      ...(data.businessLocationId ? { businessLocationId: data.businessLocationId } : {}),
                      ...(data.doctorName ? { doctorName: data.doctorName } : {}),
                      ...(data.serviceAreaId ? { serviceAreaId: data.serviceAreaId } : {}),
                      ...(data.manualServiceArea ? { manualServiceArea: data.manualServiceArea } : {}),
                      ...(data.manualBereitschaftspraxis ? { manualBereitschaftspraxis: data.manualBereitschaftspraxis } : {}),
                      ...(data.manualFahrdienstgebiet ? { manualFahrdienstgebiet: data.manualFahrdienstgebiet } : {}),
                      ...(data.bereitschaftspraxisId ? { bereitschaftspraxisId: data.bereitschaftspraxisId } : {}),
                      ...(data.fahrdienstgebietId ? { fahrdienstgebietId: data.fahrdienstgebietId } : {}),
                      ...(data.hasNotfallscheinbeteiligung ? { hasNotfallscheinbeteiligung: data.hasNotfallscheinbeteiligung } : {}),
                      ...(data.notfallscheinbeteiligungRate ? { notfallscheinbeteiligungRate: data.notfallscheinbeteiligungRate } : {}),
                    });

                    if (pendingServiceData?.draftId) {
                      await consumeProviderServiceDraft({
                        draftId: pendingServiceData.draftId,
                        dutyId,
                      });
                    }

                    trackServiceCreated(String(dutyId), analyticsPayload);
                    setShowNewModal(false);
                    setPendingServiceData(undefined);
                  } catch (error) {
                    trackServiceCreationFailed(error, analyticsPayload);
                    throw error;
                  }
                }}
                initialData={pendingServiceData}
              />
              <BulkImportModal open={showBulkImportModal} onOpenChange={(v) => setShowBulkImportModal(v)} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className='px-4 sm:px-6 py-2 sm:py-6 max-w-6xl mx-auto'>
        <div className='space-y-3 sm:space-y-6'>
          {latestDraft && (
            <section className='rounded-xl border border-border bg-card p-4 sm:p-5'>
              <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <div className='space-y-1'>
                  <p className='text-sm font-semibold text-foreground'>Website-Entwurf noch nicht veröffentlicht</p>
                  <p className='text-sm text-muted-foreground'>Ihr anonymer Website-Eintrag wartet hier auf die letzten Pflichtangaben.</p>
                </div>
                <Button onClick={() => openDraftInModal(latestDraft)} size='sm'>
                  Entwurf fortsetzen
                </Button>
              </div>
            </section>
          )}

          {/* Filters Section */}
          <ServiceFiltersToolbar
            filters={filters}
            setSearch={setSearch}
            setStatus={setStatus}
            setServiceType={setServiceType}
            setDateRange={setDateRange}
            hasActiveFilters={hasActiveFilters}
            clearFilters={clearFilters}
            filteredCount={filteredCount}
            totalCount={totalCount}
          />

          {/* Empty state when filters return no results */}
          {noFilteredResults && (
            <div className='rounded-lg border border-border bg-card p-8 text-center'>
              <p className='text-sm text-muted-foreground mb-4'>Keine Dienste gefunden. Versuchen Sie, Ihre Filter anzupassen.</p>
              <Button variant='outline' size='sm' onClick={clearFilters}>
                Filter zurücksetzen
              </Button>
            </div>
          )}

          {/* First-run consolidated empty state */}
          {isFirstRun && (
            <section className='rounded-lg border border-border bg-card px-6 py-12 text-center'>
              <div className='mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10'>
                <Briefcase className='size-6 text-primary' aria-hidden='true' />
              </div>
              <h2 className='mt-4 text-lg font-semibold text-foreground'>Noch keine Dienste</h2>
              <p className='mx-auto mt-1 max-w-sm text-sm text-muted-foreground'>Stellen Sie Ihren ersten Dienst ein, um qualifizierte Vertretungen in Ihrer Region zu finden.</p>
              <Button onClick={() => setShowNewModal(true)} className='mt-6 inline-flex items-center gap-2'>
                <Plus className='h-4 w-4' />
                Dienst einstellen
              </Button>
            </section>
          )}

          {/* Active Duties Section */}
          {!noFilteredResults && !isFirstRun && (
            <section>
              <SectionHeader title='Aktive Dienste' count={active.length} icon={Briefcase} variant='active' />
              {isLoading ? (
                <div className='grid gap-4 sm:grid-cols-1 lg:grid-cols-2 [&>*]:min-w-0'>
                  {[1, 2, 3].map((i) => (
                    <ServiceCardSkeleton key={i} />
                  ))}
                </div>
              ) : active.length > 0 ? (
                <div className='grid gap-4 sm:grid-cols-1 lg:grid-cols-2 [&>*]:min-w-0'>
                  {active.map((d: any) => (
                    <ServiceCardPremium
                      key={d._id}
                      duty={d}
                      metrics={
                        metricsMap.get(d._id) || {
                          applicationCount: 0,
                          documentCount: 0,
                          hasUnreadMessages: false,
                        }
                      }
                    />
                  ))}
                </div>
              ) : (
                <EmptyState variant='active' hasFilters={hasActiveFilters} />
              )}
            </section>
          )}

          {/* Past Duties Section */}
          {!noFilteredResults && !isFirstRun && (
            <section>
              <SectionHeader title='Vergangene Dienste' count={pastNotCompleted.length} icon={Clock} variant='past' />
              {isLoading ? (
                <div className='grid gap-4 sm:grid-cols-1 lg:grid-cols-2 [&>*]:min-w-0'>
                  {[1, 2].map((i) => (
                    <ServiceCardSkeleton key={i} />
                  ))}
                </div>
              ) : pastNotCompleted.length > 0 ? (
                <div className='grid gap-4 sm:grid-cols-1 lg:grid-cols-2 [&>*]:min-w-0'>
                  {pastNotCompleted.map((d: any) => (
                    <ServiceCardPremium
                      key={d._id}
                      duty={d}
                      metrics={
                        metricsMap.get(d._id) || {
                          applicationCount: 0,
                          documentCount: 0,
                          hasUnreadMessages: false,
                        }
                      }
                    />
                  ))}
                </div>
              ) : (
                <EmptyState variant='past' hasFilters={hasActiveFilters} />
              )}
            </section>
          )}

          {/* Completed Duties Section */}
          {!noFilteredResults && !isFirstRun && (
            <section>
              <SectionHeader title='Abgeschlossene Dienste' count={completed.length} icon={CheckCircle2} variant='completed' />
              {isLoading ? (
                <div className='grid gap-4 sm:grid-cols-1 lg:grid-cols-2 [&>*]:min-w-0'>
                  {[1, 2].map((i) => (
                    <ServiceCardSkeleton key={i} />
                  ))}
                </div>
              ) : completed.length > 0 ? (
                <div className='grid gap-4 sm:grid-cols-1 lg:grid-cols-2 [&>*]:min-w-0'>
                  {completed.map((d: any) => (
                    <ServiceCardPremium
                      key={d._id}
                      duty={d}
                      metrics={
                        metricsMap.get(d._id) || {
                          applicationCount: 0,
                          documentCount: 0,
                          hasUnreadMessages: false,
                        }
                      }
                    />
                  ))}
                </div>
              ) : (
                <EmptyState variant='completed' hasFilters={hasActiveFilters} />
              )}
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export const Route = createFileRoute('/_protected/provider/services')({
  component: ProviderServicesPage,
});
